/**
 * Planning App — Main router for Planning Mode (canon 10–17).
 *
 * Routes:
 * /planning — Mode-switched surface (Doc 26): B = live dashboard workbench,
 *             C = feasibility answer ("Can we deliver our priorities?")
 * /planning/plans — smart landing → primary plan cycle (Plan of Record home)
 * /planning/plans?catalog=1 — plan cycle catalog (set primary / browse)
 * /planning/plans/:cycleId — Plan of Record overview (initiatives, health, NRW)
 * /planning/plans/:cycleId/commitments/:itemId — canonical commitment view (commitment_view gate)
 * /planning/plan-of-record/* — org narrative instrument (secondary; DEP-021 redirects entry)
 * /planning/work-graph — Work Graph
 * /planning/capacity — Capacity
 * /planning/milestones — Milestones
 * /planning/assumptions-risks — Assumptions & Risks
 * /planning/changes — Changes (Diff)
 * /planning/governance — Governance / Audit
 * /planning/spreadsheet — Plan Workbench (6-sheet plan-cycle workbench)
 *
 * NOTE: Roadmaps (/planning/roadmap) were moved to /prioritization/roadmap.
 * NOTE: Plan of Record is mounted under both shells; Planning uses /planning/plan-of-record/*.
 * Old /planning/roadmap/* URLs redirect automatically.
 */

import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import PlanningLayout from '../../components/planning/PlanningLayout';
import PlanningEngineApp from './engine/PlanningEngineApp';
import PlanningSurface from './PlanningSurface';
import PlanDetailLayout from './PlanDetailLayout';
import PlanningPlanDetail from './PlanningPlanDetail';
import PlanNarrativePage from './PlanNarrativePage';
import PlanningTermsClaims from './PlanningTermsClaims';
import { CommitmentViewRoute } from './CommitmentViewPage';
import PlanningPlansSmartEntry from './PlanningPlansSmartEntry';
import PorRoutes from '../plan-of-record/PorRoutes';
import PlanningWorkGraph from './PlanningWorkGraph';
import PlanningCapacity from './PlanningCapacity';
import PlanningMilestones from './PlanningMilestones';
import PlanningAssumptionsRisks from './PlanningAssumptionsRisks';
import PlanningChanges from './PlanningChanges';
import PlanningGovernance from './PlanningGovernance';
import PlanningAreas from './PlanningAreas';
import PlanningPeopleCapacity from './PlanningPeopleCapacity';
import PlanningSpreadsheet from './PlanningSpreadsheet';
import LaunchCalendarPage from './LaunchCalendarPage';
function PlanningAppRoutes() {
  const location = useLocation();

  // Redirect old /planning/roadmap/:id/print → /prioritization/roadmap/:id/print
  const printMatch = location.pathname.match(/^\/planning\/roadmap\/([^/]+)\/print\/?$/);
  if (printMatch) {
    return <Navigate to={`/prioritization/roadmap/${printMatch[1]}/print`} replace />;
  }

  return (
    <PlanningLayout>
      <Routes>
        {/* Landing: B/C mode switch — live workbench or feasibility answer (Doc 26) */}
        <Route index element={<PlanningSurface />} />
        {/* Legacy /planning/dashboard → same mode-switched surface */}
        <Route path="dashboard" element={<PlanningSurface />} />
        {/* Plan of Record: smart → primary cycle; catalog at ?catalog=1 */}
        <Route path="plans" element={<PlanningPlansSmartEntry />} />

        {/*
         * Plan-scoped routes: /planning/plans/:cycleId/*
         * Overview (PlanningPlanDetail) is the product Plan of Record home.
         */}
        <Route path="plans/:cycleId" element={<PlanDetailLayout />}>
          <Route index element={<PlanningPlanDetail />} />
          <Route path="milestones" element={<PlanningMilestones />} />
          <Route path="work-graph" element={<PlanningWorkGraph />} />
          <Route path="capacity" element={<PlanningCapacity />} />
          <Route path="governance" element={<PlanningGovernance />} />
          <Route path="assumptions-risks" element={<PlanningAssumptionsRisks />} />
          <Route path="changes" element={<PlanningChanges />} />
          {/* Unified PoR narrative surface (DEP-026, plan_narrative gate) */}
          <Route path="narrative/*" element={<PlanNarrativePage />} />
          <Route path="terms" element={<PlanningTermsClaims />} />
          {/*
           * Canonical commitment view (PRD P3, commitment_view gate). Mounted
           * under the plan-scoped routes rather than at a top-level
           * /commitments/:id, per the PRD's open question 1: the commitment's
           * durable identity IS the (plan cycle, item) pair, so the plan-scoped
           * URL expresses the identity instead of encoding it. One gated route
           * — there is deliberately no second, ungated URL for this surface.
           */}
          <Route path="commitments/:itemId" element={<CommitmentViewRoute />} />
        </Route>

        {/* Org narrative instrument — index redirects; deep links kept for bottom-card links */}
        <Route path="plan-of-record/*" element={<PorRoutes />} />

        {/* Workspace-level pages (not plan-scoped) */}
        <Route path="work-graph" element={<PlanningWorkGraph />} />
        <Route path="capacity" element={<PlanningCapacity />} />
        {/* Unified People & Capacity (replaces Capabilities + Capacity Pool + People & Capabilities) */}
        <Route path="people-capacity" element={<PlanningPeopleCapacity />} />
        <Route path="areas" element={<PlanningAreas />} />
        {/* Legacy routes — redirect to their replacements */}
        <Route path="capabilities" element={<Navigate to="/planning/people-capacity" replace />} />
        <Route path="people-capabilities" element={<Navigate to="/planning/people-capacity" replace />} />
        <Route path="capacity-pool" element={<Navigate to="/planning/people-capacity" replace />} />
        {/* Workspace-level ghost routes — now accessible but still no sidebar entry */}
        <Route path="milestones" element={<PlanningMilestones />} />
        <Route path="assumptions-risks" element={<PlanningAssumptionsRisks />} />
        <Route path="changes" element={<PlanningChanges />} />
        <Route path="governance" element={<PlanningGovernance />} />
        <Route path="spreadsheet" element={<PlanningSpreadsheet />} />
        <Route path="launch-calendar" element={<LaunchCalendarPage />} />
        {/* plan-dashboard → merged into live dashboard */}
        <Route path="plan-dashboard" element={<Navigate to="/planning" replace />} />
        <Route path="engine/*" element={<PlanningEngineApp />} />
        {/* Roadmap moved to /prioritization/roadmap — redirect legacy links */}
        <Route path="roadmap" element={<Navigate to="/prioritization/roadmap" replace />} />
        <Route path="roadmap/:id" element={<RoadmapIdRedirect />} />
        <Route path="*" element={<Navigate to="/planning" replace />} />
      </Routes>
    </PlanningLayout>
  );
}

/** Redirects /planning/roadmap/:id → /prioritization/roadmap/:id preserving the id. */
function RoadmapIdRedirect() {
  const location = useLocation();
  const id = location.pathname.split('/').at(-1) ?? '';
  return <Navigate to={`/prioritization/roadmap/${id}`} replace />;
}

export default function PlanningApp() {
  return <PlanningAppRoutes />;
}
